import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { IntlContext } from '@/i18n';
import { IPartner } from '@/modules/partner/model';
import { CountryCode } from '@/shared/models/country-code';
import { DescriptionList } from '@/shared/view/DescriptionList';

import { messages } from './paymentMessages';
import s from './styles.module.css';

interface IProps {
  partner: IPartner;
  className: string;
  onClickEdit: () => void;
}

const isEmpty = (x: Record<string, any>) => Object.values(x).filter(Boolean).length === 0;

const mapToPaymentInfo = (partner: IPartner) => ({
  accountNum: partner.paymentAccount,
  rasAccount: partner.paymentRasAccount,
  swift: partner.paymentSwift,
  iban: partner.paymentIban,
  sortCode: partner.paymentSortCode,
  routingNumber: partner.paymentRoutingNumber,
  bik: partner.paymentBik,
  bankName: partner.paymentBankName,
  correspondentAccount: partner.paymentKorAccount,
});

type TTitleLine = [keyof ReturnType<typeof mapToPaymentInfo>, string];

const PaymentInfo = ({ className, partner, onClickEdit }: IProps) => {
  const intl = useIntl();
  const { region } = useContext(IntlContext);

  const titles = useMemo((): Array<TTitleLine> => {
    switch (region) {
      case CountryCode.Russia:
        return [
          ['rasAccount', intl.formatMessage(messages.accountNumberTitle)],
          ['bik', intl.formatMessage(messages.ruBicTitle)],
          ['bankName', intl.formatMessage(messages.bankNameTitle)],
          ['correspondentAccount', intl.formatMessage(messages.correspondentAccountTitle)],
        ];
      case CountryCode.UnitedStates:
        return [
          ['accountNum', intl.formatMessage(messages.accountNumberTitle)],
          ['routingNumber', intl.formatMessage(messages.routingNumberTitle)],
          ['bankName', intl.formatMessage(messages.bankNameTitle)],
        ];
      case CountryCode.France:
        return [
          ['swift', intl.formatMessage(messages.swiftTitle)],
          ['iban', intl.formatMessage(messages.ibanTitle)],
        ];
      case CountryCode.ArabEmirates:
        return [
          ['iban', intl.formatMessage(messages.ibanTitle)],
          ['bik', intl.formatMessage(messages.uaeBicTitle)],
        ];
      default:
        return [
          ['accountNum', intl.formatMessage(messages.accountNumberTitle)],
          ['sortCode', intl.formatMessage(messages.sortCodeTitle)],
        ];
    }
  }, [intl, region]);

  const values = useMemo(() => mapToPaymentInfo(partner), [partner]);
  const paymentInfoIsEmpty = isEmpty(values);

  return (
    <DescriptionList
      title={intl.formatMessage(messages.title)}
      className={className}
      values={values}
      titles={titles}
      action={
        <span onClick={onClickEdit} className={s.editButton}>
          {paymentInfoIsEmpty
            ? intl.formatMessage(messages.addAction)
            : intl.formatMessage(messages.editAction)}
        </span>
      }
    />
  );
};

export { PaymentInfo };
